import React, { CSSProperties, useEffect, useRef, useState } from "react"

import { AudioUsecase } from "@/usecases/audio"
import microphoneOffIcon from "@/assest/ic_microphone_off.svg"
import microphoneOnIcon from "@/assest/ic_microphone_on.svg"
import { Flex, Switch, Text, ToastId, useToast } from "@chakra-ui/react"

interface AudioContentProps {
  itemStyle?: CSSProperties
}

const AudioContent: React.FC<AudioContentProps> = ({ itemStyle }) => {
  // 是否启用录音设备
  const [isAudioInputEnable, setAudioInputEnable] = useState<boolean>(
    AudioUsecase.isAudioInputEnabled()
  )

  // null 表示设备列表仍在异步检测，避免检测完成前短暂显示“暂无设备”。
  const [hasInputDev, setHasInputDev] = useState<boolean | null>(null)
  const [isAudioInputOperating, setAudioInputOperating] =
    useState<boolean>(false)
  const toast = useToast()
  const audioToastId = useRef<ToastId | null>(null)

  const showAudioLoading = (title: string) => {
    if (audioToastId.current !== null && toast.isActive(audioToastId.current)) {
      toast.close(audioToastId.current)
    }

    audioToastId.current = toast({
      title,
      status: "loading",
      duration: null,
      isClosable: true,
      position: "top-right",
      containerStyle: {
        width: "190px",
      },
    })
  }

  const showAudioResult = (
    title: string,
    status: "success" | "error" = "success",
    icon?: React.ReactNode
  ) => {
    const options = {
      title,
      status,
      icon,
      duration: 2000,
      isClosable: true,
      position: "top-right" as const,
      containerStyle: {
        width: "190px",
      },
    }

    if (audioToastId.current !== null && toast.isActive(audioToastId.current)) {
      toast.update(audioToastId.current, options)
      return
    }

    audioToastId.current = toast(options)
  }

  const waitForLoadingVisible = async (startedAt: number) => {
    const minLoadingMs = 500
    const elapsedMs = Date.now() - startedAt
    if (elapsedMs >= minLoadingMs) {
      return
    }

    await new Promise((resolve) => {
      window.setTimeout(resolve, minLoadingMs - elapsedMs)
    })
  }

  // 当前录音设备音量
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [inputDevVolume, setInputDevVolume] = useState<number | undefined>(0)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [outDevice, setOutDevice] = useState<string | null>("HDMI") // 输出-设备
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [outVoice, setOutVoice] = useState<number | undefined>(30) // 输出-音量

  // 选择输入设备
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEnterDevice = (devId: string) => {}

  // 处理输入音量
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEnterVoice = (e: number) => {
    setInputDevVolume(e)
  }

  // 选择输出设备
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleOutDevice = (e: string) => {
    setOutDevice(e)
  }

  // 处理输出音量
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleOutVoice = (e: number) => {
    setOutVoice(e)
  }
  useEffect(() => {
    const init = async () => {
      // 获取音频输入设备
      const devices = await AudioUsecase.getAudioInputDevices()

      if (devices !== null && devices.length > 0) {
        setHasInputDev(true)

        if (
          AudioUsecase.isAudioInputEnabled() &&
          AudioUsecase.getAudioInputStream() === null
        ) {
          const isOpen = await AudioUsecase.openAudioInputStream()
          setAudioInputEnable(isOpen)
        }
      } else {
        setHasInputDev(false)
        setAudioInputEnable(false)
        await AudioUsecase.closeAudioInputStream()
      }
    }

    init().catch((error) => {
      console.error("Error initializing audio device:", error)
      setHasInputDev(false)
      setAudioInputEnable(false)
    })
    // 音频输入设备变化 监听器
    const handleAudioDeviceChange = () => {
      init().catch((error) => {
        console.error("Error handling audio device change:", error)
      })
    }
    navigator.mediaDevices?.addEventListener?.(
      "devicechange",
      handleAudioDeviceChange
    )
    const removeAudioStateListener =
      AudioUsecase.addAudioInputStateListener(setAudioInputEnable)

    return () => {
      navigator.mediaDevices?.removeEventListener?.(
        "devicechange",
        handleAudioDeviceChange
      )
      removeAudioStateListener()
    }
  }, [])

  const handleAudioInputEnableChange = async (enable: boolean) => {
    if (isAudioInputOperating) {
      return
    }

    setAudioInputOperating(true)
    try {
      if (enable) {
        // 打开音频输入流
        const startedAt = Date.now()
        showAudioLoading("正在打开麦克风")
        const isOpen = await AudioUsecase.openAudioInputStream()
        await waitForLoadingVisible(startedAt)
        setAudioInputEnable(isOpen)
        showAudioResult(
          isOpen ? "麦克风已开启" : "麦克风开启失败",
          isOpen ? "success" : "error",
          isOpen ? (
            <img src={microphoneOnIcon} alt="" width="20" height="20" />
          ) : undefined
        )
      } else {
        // 关闭音频输入流
        const startedAt = Date.now()
        showAudioLoading("正在关闭麦克风")
        await AudioUsecase.closeAudioInputStream()
        await waitForLoadingVisible(startedAt)
        setAudioInputEnable(false)
        showAudioResult(
          "麦克风已关闭",
          "success",
          <img src={microphoneOffIcon} alt="" width="20" height="20" />
        )
      }
    } catch (error) {
      console.error("Error changing audio input state:", error)
      showAudioResult(enable ? "麦克风开启失败" : "麦克风关闭失败", "error")
    } finally {
      setAudioInputOperating(false)
    }
  }

  return (
    <Flex direction="column">
      <Flex
        direction={"row"}
        justifyContent={"space-between"}
        alignItems="center"
        style={itemStyle}
        minHeight="34px"
      >
        <Text color="#FFFFFF" fontSize="13px" flexShrink={0}>
          麦克风
        </Text>
        <div>
          {hasInputDev === false ? (
            <span
              style={{
                backgroundColor: "gray",
                color: "white",
                fontSize: "12px",
                padding: "3px 7px 3px 7px",
                borderRadius: "3px",
                marginRight: "3px",
              }}
            >
              暂无可用的麦克风
            </span>
          ) : (
            <span></span>
          )}
          <Switch
            isChecked={isAudioInputEnable}
            isDisabled={hasInputDev !== true || isAudioInputOperating}
            onChange={(event) => {
              handleAudioInputEnableChange(event.target.checked)
            }}
          />
        </div>
      </Flex>
    </Flex>
  )
}

export default AudioContent
